import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Users, UserPlus, Dumbbell, Clock, CheckCircle, XCircle,
  ChevronRight, MessageSquare, Activity, BarChart3, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';

const StatCard = ({ icon, label, value, color = 'text-primary' }) => (
  <div className="hpme-card p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="hpme-card-corner" />
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-sm bg-zinc-800 flex items-center justify-center ${color}`}>
        {icon}
      </div>
      <div>
        <p className="hpme-number text-2xl">{value}</p>
        <p className="text-xs text-zinc-500 uppercase tracking-wider">{label}</p>
      </div>
    </div>
  </div>
);

const RequestCard = ({ request, onAccept, onDecline, loading }) => (
  <div className="hpme-card p-4 flex items-center justify-between" data-testid={`request-${request.id}`}>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
        <span className="text-sm font-bold text-yellow-400">{request.athlete_name?.charAt(0) || 'A'}</span>
      </div>
      <div>
        <p className="font-semibold text-sm">{request.athlete_name}</p>
        {request.message && <p className="text-xs text-zinc-500 mt-0.5">{request.message}</p>}
        <p className="text-xs text-zinc-500 mt-0.5">
          <Clock className="w-3 h-3 inline mr-1" />
          {new Date(request.created_at).toLocaleDateString()}
        </p>
      </div>
    </div>
    <div className="flex gap-2">
      <Button size="sm" className="hpme-btn-primary h-8 text-xs" onClick={() => onAccept(request.id)} disabled={loading} data-testid={`accept-${request.id}`}>
        <CheckCircle className="w-3.5 h-3.5 mr-1" /> Accept
      </Button>
      <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-red-400 h-8 text-xs" onClick={() => onDecline(request.id)} disabled={loading} data-testid={`decline-${request.id}`}>
        <XCircle className="w-3.5 h-3.5" />
      </Button>
    </div>
  </div>
);

const AthleteCard = ({ athlete, onRemove }) => (
  <div
    className="hpme-card p-4 flex items-center justify-between hover:border-primary/50 transition-colors"
    data-testid={`athlete-${athlete.athlete_id}`}
  >
    <Link 
      to={`/coach/athlete/${athlete.athlete_id}`}
      className="flex items-center gap-3 flex-1 cursor-pointer"
    >
      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
        <span className="text-sm font-bold text-primary">{athlete.athlete_name?.charAt(0) || 'A'}</span>
      </div>
      <div>
        <p className="font-semibold text-sm">{athlete.athlete_name}</p>
        <p className="text-xs text-zinc-500">
          Connected {athlete.started_at ? new Date(athlete.started_at).toLocaleDateString() : ''}
        </p>
      </div>
    </Link>
    <div className="flex items-center gap-2">
      <button
        onClick={(e) => { e.preventDefault(); onRemove(athlete.athlete_id, athlete.athlete_name); }}
        className="p-2 text-zinc-600 hover:text-red-400 transition-colors"
        title="Disconnect athlete"
        data-testid={`remove-athlete-${athlete.athlete_id}`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
      <ChevronRight className="w-4 h-4 text-zinc-500" />
    </div>
  </div>
);

const CoachDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total_workouts_created: 0, total_sessions_using_workouts: 0, completed_sessions: 0 });
  const [athletes, setAthletes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [profile, setProfile] = useState(null);
  const [hasProfile, setHasProfile] = useState(true);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, athletesRes, requestsRes] = await Promise.allSettled([
        api.get('/coach/stats'),
        api.get('/coach/athletes'),
        api.get('/coach/requests/incoming')
      ]);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (athletesRes.status === 'fulfilled') setAthletes(athletesRes.value.data);
      if (requestsRes.status === 'fulfilled') setRequests(requestsRes.value.data);
    } catch (err) {
      console.error('Failed to load coach data:', err);
    }
    try {
      const profileRes = await api.get('/coach/profile/me');
      setProfile(profileRes.data);
      setHasProfile(true);
    } catch {
      setHasProfile(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAccept = async (requestId) => {
    setLoading(true);
    try {
      await api.post(`/coach/requests/${requestId}/accept`);
      toast.success('Athlete accepted!');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    } finally { setLoading(false); }
  };

  const handleDecline = async (requestId) => {
    setLoading(true);
    try {
      await api.post(`/coach/requests/${requestId}/decline`);
      toast.success('Request declined');
      loadData();
    } catch (err) {
      toast.error('Failed to decline');
    } finally { setLoading(false); }
  };

  const handleRemoveAthlete = async (athleteId, athleteName) => {
    if (!window.confirm(`Disconnect ${athleteName}? This will remove them from your athletes list.`)) return;
    try {
      await api.delete(`/coach/athletes/${athleteId}`);
      toast.success(`${athleteName} disconnected`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disconnect');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-8" data-testid="coach-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-secondary font-bold tracking-tight">
            COACH DASHBOARD
          </h1>
          <p className="text-zinc-400 mt-1">Welcome back, {user?.name || 'Coach'}</p>
        </div>

        {/* Profile setup prompt */}
        {!hasProfile && (
          <div className="hpme-card p-6 border-yellow-500/30 bg-yellow-500/5" data-testid="setup-profile-prompt">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                <UserPlus className="w-6 h-6 text-yellow-400" />
              </div>
              <div className="flex-1">
                <h3 className="font-secondary font-bold text-lg">Complete Your Coach Profile</h3>
                <p className="text-zinc-400 text-sm mt-1">Set up your profile so athletes can find you in the marketplace.</p>
                <Link to="/coach/profile-setup">
                  <Button className="hpme-btn-primary mt-3" data-testid="setup-profile-btn">
                    Set Up Profile <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={<Users className="w-5 h-5" />} label="Athletes" value={athletes.length} />
          <StatCard icon={<UserPlus className="w-5 h-5" />} label="Pending Requests" value={requests.length} color="text-yellow-400" />
          <StatCard icon={<Dumbbell className="w-5 h-5" />} label="Workouts Created" value={stats.total_workouts_created} color="text-accent-blue" />
          <StatCard icon={<BarChart3 className="w-5 h-5" />} label="Sessions Completed" value={stats.completed_sessions} color="text-green-400" />
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Incoming Requests */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-secondary font-bold text-lg tracking-wider">
                INCOMING REQUESTS
                {requests.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-sm">{requests.length}</span>
                )}
              </h2>
            </div>
            {requests.length === 0 ? (
              <div className="hpme-card p-8 text-center">
                <MessageSquare className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">No pending requests</p>
                <p className="text-zinc-500 text-xs mt-1">Athletes can find you in the coach marketplace</p>
              </div>
            ) : (
              <div className="space-y-3">
                {requests.map(r => (
                  <RequestCard key={r.id} request={r} onAccept={handleAccept} onDecline={handleDecline} loading={loading} />
                ))}
              </div>
            )}
          </div>

          {/* My Athletes */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-secondary font-bold text-lg tracking-wider">MY ATHLETES</h2>
              <span className="text-xs text-zinc-500">{athletes.length} connected</span>
            </div>
            {athletes.length === 0 ? (
              <div className="hpme-card p-8 text-center">
                <Users className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">No athletes yet</p>
                <p className="text-zinc-500 text-xs mt-1">Athletes will appear here once they connect with you</p>
              </div>
            ) : (
              <div className="space-y-3">
                {athletes.map(a => <AthleteCard key={a.athlete_id || a.id} athlete={a} onRemove={handleRemoveAthlete} />)}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="font-secondary font-bold text-lg tracking-wider mb-4">QUICK ACTIONS</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link to="/coach" className="hpme-card p-5 hover:border-primary/50 transition-colors group" data-testid="action-create-workout">
              <div className="hpme-card-corner" />
              <Dumbbell className="w-6 h-6 text-primary mb-3" />
              <p className="font-secondary font-bold">Create Workout</p>
              <p className="text-xs text-zinc-500 mt-1">Build a custom plan for your athletes</p>
              <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-primary mt-2 transition-colors" />
            </Link>
            <Link to="/live-sessions" className="hpme-card p-5 hover:border-primary/50 transition-colors group" data-testid="action-live-session">
              <div className="hpme-card-corner" />
              <Activity className="w-6 h-6 text-accent-blue mb-3" />
              <p className="font-secondary font-bold">Live Session</p>
              <p className="text-xs text-zinc-500 mt-1">Start a real-time coaching session</p>
              <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-accent-blue mt-2 transition-colors" />
            </Link>
            <div 
              onClick={() => { if (athletes.length > 0) window.location.href = `/coach/athlete/${athletes[0].athlete_id}`; else toast.info('Connect with an athlete first to view their stats'); }}
              className="hpme-card p-5 hover:border-primary/50 transition-colors group cursor-pointer" 
              data-testid="action-view-stats"
            >
              <div className="hpme-card-corner" />
              <BarChart3 className="w-6 h-6 text-green-400 mb-3" />
              <p className="font-secondary font-bold">View Stats</p>
              <p className="text-xs text-zinc-500 mt-1">Click an athlete above to see their data</p>
              <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-green-400 mt-2 transition-colors" />
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CoachDashboard;
